import React from 'react';
import {
  FlatList,
  Text,
  TouchableOpacity,
  View,
  StyleSheet,
} from 'react-native';
import {Colors} from '../../../constants/Colors';
import ProductList from '../components/ProductList/ProductList';
import LegacyProductList from '../../../components/SugestedProducts/ProductList/ProductList';

interface ProductCarouselShellProps {
  title: string;
  emoji?: string;
  products: any[] | null;
  loading?: boolean;
  onSeeAll?: () => void;
  seeAllLabel?: string;
  minItemsForSeeAll?: number;
  onAddBag: (product: any, quantity: number) => void;
  onOpenProduct: (product: any) => void;
  headerRight?: React.ReactNode;
  useLegacyCard?: boolean;
  clientCode?: string;
}

const ProductCarouselShell: React.FC<ProductCarouselShellProps> = ({
  title,
  emoji,
  products,
  loading = false,
  onSeeAll,
  seeAllLabel = 'Ver todos',
  minItemsForSeeAll = 8,
  onAddBag,
  onOpenProduct,
  headerRight,
  useLegacyCard = false,
  clientCode,
}) => {
  const showSeeAll = onSeeAll && (products?.length ?? 0) >= minItemsForSeeAll;
  // Stride = card width + marginRight, so the carousel snaps to each card's
  // start instead of resting mid-card. Legacy card: 160+10; carousel card: 153+10.
  const cardStride = useLegacyCard ? 170 : 163;

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={skeletonTitleStyle} />
        </View>
        <View style={skeletonRowStyle}>
          {[0, 1, 2].map(i => (
            <View key={i} style={skeletonCardContainerStyle}>
              <View style={skeletonImageStyle} />
              <View style={skeletonLineStyle} />
              <View style={[skeletonLineStyle, {width: '55%', height: 10, marginTop: 6}]} />
              <View style={[skeletonLineStyle, {width: '40%', height: 12, marginTop: 6}]} />
            </View>
          ))}
        </View>
      </View>
    );
  }

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>
          {emoji ? `${emoji} ` : ''}{title}
        </Text>
        <View style={styles.headerActions}>
          {headerRight}
          {showSeeAll && (
            <TouchableOpacity onPress={onSeeAll} style={styles.seeAllButton}>
              <Text style={styles.seeAllText}>{seeAllLabel} →</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
      <FlatList
        horizontal
        showsHorizontalScrollIndicator={false}
        snapToInterval={cardStride}
        snapToAlignment="start"
        decelerationRate="fast"
        data={products}
        contentContainerStyle={styles.listContent}
        renderItem={({item}) =>
          useLegacyCard ? (
            <LegacyProductList
              item={item}
              image={item?.image}
              title={item?.custom_title || item?.descricao}
              description={
                (item?.custom_title || item?.descricao) +
                ' ' +
                (item?.complemento || '').replace('- R$', '').replace(/\\n/g, '')
              }
              AddBag={(quantity: number) => onAddBag(item, quantity)}
              onPress={() => onOpenProduct(item)}
            />
          ) : (
            <ProductList
              item={item}
              image={item?.image}
              title={item?.custom_title || item?.descricao}
              description={
                (item?.custom_title || item?.descricao) +
                ' ' +
                (item?.complemento || '').replace('- R$', '').replace(/\\n/g, '')
              }
              AddBag={(quantity: number) => onAddBag(item, quantity)}
              onPress={() => onOpenProduct(item)}
              clientCode={clientCode}
              containerStyle={carouselCardStyle}
            />
          )
        }
        keyExtractor={(item, index) =>
          item?.sku ? `${item.sku}-${index}` : String(index)
        }
      />
    </View>
  );
};

const skeletonTitleStyle = {
  width: 140,
  height: 16,
  backgroundColor: '#e0e0e0',
  borderRadius: 4,
};

const skeletonRowStyle = {
  flexDirection: 'row' as const,
  paddingHorizontal: 12,
  gap: 10,
};

const skeletonCardContainerStyle = {
  width: 153,
  backgroundColor: '#f0f0f0',
  borderRadius: 8,
  padding: 10,
};

const skeletonImageStyle = {
  width: '100%' as const,
  height: 160,
  backgroundColor: '#e0e0e0',
  borderRadius: 4,
};

const skeletonLineStyle = {
  width: '80%' as const,
  height: 12,
  backgroundColor: '#e0e0e0',
  borderRadius: 3,
  marginTop: 8,
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFF',
    paddingTop: 15,
    paddingBottom: 10,
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
    marginBottom: 5,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    marginBottom: 12,
  },
  title: {
    fontSize: 15,
    fontFamily: 'TCCC-UnityText Bold',
    color: Colors.RedPallete.red,
    flex: 1,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  seeAllButton: {
    marginLeft: 8,
  },
  seeAllText: {
    fontSize: 13,
    color: Colors.RedPallete.red,
    fontFamily: 'TCCC-UnityHeadline Regular',
  },
  listContent: {
    paddingHorizontal: 12,
  },
});

const carouselCardStyle = {
  width: 153,
  margin: 0,
  marginRight: 10,
  minHeight: 380,
};

export default ProductCarouselShell;
